import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, CheckCircle, Building2, Users, Mail, Phone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const companySize = [
  { value: '1-50', label: '1-50 employees' },
  { value: '51-250', label: '51-250 employees' },
  { value: '251-500', label: '251-500 employees' },
  { value: '501-1000', label: '501-1,000 employees' },
  { value: '1001+', label: '1,000+ employees' }
];

const interests = [
  'Employee onboarding/offboarding',
  'Access request automation',
  'Device compliance',
  'Security incident response',
  'ITSM integration',
  'Compliance and audit',
  'Other'
];

const whatToExpect = [
  {
    step: '1',
    title: 'Brief Discovery Call',
    description: '15-minute call to understand your current IT operations and goals.'
  },
  {
    step: '2',
    title: 'Personalized Demo',
    description: '30-minute demo tailored to your use cases and systems.'
  },
  {
    step: '3',
    title: 'Pilot Planning',
    description: 'If there\'s a fit, we scope a 14-day pilot with clear success metrics.'
  }
];

export const RequestDemo = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    companySize: '',
    jobTitle: '',
    phone: '',
    interests: [],
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <MarketingLayout>
        <div className="min-h-screen flex items-center justify-center px-6 py-16">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-4">Request Received</h1>
            <p className="text-muted-foreground mb-8">
              Thanks for your interest in Vedaya. A member of our team will reach out within
              one business day to schedule your demo.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild variant="outline">
                <Link to="/">Back to Home</Link>
              </Button>
              <Button asChild>
                <Link to="/control-tower">
                  Explore Control Tower
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-12 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Request Demo</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              Request a Demo
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              See how Vedaya automates IT operations with a personalized demo tailored to your use cases.
            </p>
          </div>
        </section>

        {/* Form and Info */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8">
              {/* Form */}
              <div className="md:col-span-3">
                <form onSubmit={handleSubmit} className="bg-white border border-border rounded-lg p-6">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-1">Work Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Company *</label>
                      <input
                        type="text"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Company Size *</label>
                      <select
                        name="companySize"
                        required
                        value={formData.companySize}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      >
                        <option value="">Select...</option>
                        {companySize.map(size => (
                          <option key={size.value} value={size.value}>{size.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Job Title</label>
                      <input
                        type="text"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-2">What are you interested in?</label>
                    <div className="flex flex-wrap gap-2">
                      {interests.map(interest => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => handleInterestChange(interest)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            formData.interests.includes(interest)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-white text-muted-foreground border-border hover:border-primary/50'
                          }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-foreground mb-1">Additional Context</label>
                    <textarea
                      name="message"
                      rows={3}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your current IT operations or specific challenges..."
                      className="w-full px-3 py-2 bg-secondary/30 border border-border rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    Request Demo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>

                  <p className="text-xs text-muted-foreground text-center mt-4">
                    By submitting this form, you agree to our{' '}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
                  </p>
                </form>
              </div>

              {/* Info */}
              <div className="md:col-span-2 space-y-6">
                <div className="bg-white border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">What to Expect</h2>
                  <div className="space-y-4">
                    {whatToExpect.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold shrink-0">
                          {item.step}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground">{item.title}</div>
                          <div className="text-sm text-muted-foreground">{item.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Contact Us Directly</h2>
                  <div className="space-y-3">
                    <a href="mailto:sales@vedaya.ai" className="flex items-center gap-3 text-sm text-foreground hover:text-primary transition-colors">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      sales@vedaya.ai
                    </a>
                    <a href="tel:+14155551234" className="flex items-center gap-3 text-sm text-foreground hover:text-primary transition-colors">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      +1 (415) 555-1234
                    </a>
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-foreground mb-2">Prefer to explore first?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Check out our interactive Control Tower demo to see Vedaya in action.
                  </p>
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to="/control-tower">
                      Open Control Tower
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default RequestDemo;
